'use client'
import * as React from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import GradientText from 'components/gradient-text/GradientText'
import classes from './styles.module.css'
import {motion} from 'framer-motion'
import {Swiper, SwiperSlide, useSwiper} from 'swiper/react'
import {EffectFade, Autoplay} from 'swiper/modules'
import {IManageHybr1dData} from 'types/hybr1d-plateform'

import 'swiper/css'
import 'swiper/css/effect-fade'

interface VerticalCarouselProps {
  data: IManageHybr1dData
}
export default function VerticalCarousel({data}: VerticalCarouselProps) {
  const [activeSlide, setActiveSlide] = React.useState<number>(0)

  const handleIndexChange = () => {
    if (activeSlide === data.slides.length - 1) {
      setActiveSlide(0)
    } else {
      setActiveSlide(prev => prev + 1)
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.subContainer}>
        <GradientText text={data.topTitle} type="secondary" customStyle={{textAlign: 'center'}} />
        <h2 className={clsx('header2', classes.header)}>{data.title}</h2>
        <p className={classes.desc}> {data.desc}</p>
        <div className={classes.tagContainer}>
          {data.tags.map((tag, idx) => (
            <div className={classes.tag} key={idx}>
              <Image className={classes.tagIcon} src={tag.icon} alt={tag.text} />
              <p className={classes.tagText}>{tag.text}</p>
            </div>
          ))}
        </div>
        <div className={classes.slidesContainer}>
          <div className={classes.progressBarParentContainer}>
            {data.slides.map((progress, index) => (
              <div
                key={progress.btnDetails.title}
                className={clsx(
                  classes.singleProgressContainer,
                  index === activeSlide ? classes.singleProgressContainerActive : '',
                )}
                onClick={() => {
                  setActiveSlide(index)
                }}
              >
                <div
                  className={clsx(
                    classes.progressText,
                    index === activeSlide ? classes.progressTextActive : '',
                  )}
                >
                  {progress.btnDetails.title}
                </div>
                {index === activeSlide && (
                  <>
                    <div className={classes.progressDesc}> {progress.btnDetails.desc}</div>
                    <LoaderBar handleIndexChange={handleIndexChange} />
                  </>
                )}
              </div>
            ))}
          </div>
          <div className={classes.imageParentContainer}>
            <Swiper
              noSwiping={true}
              allowTouchMove={false}
              className="mySwiper"
              loop={true}
              effect={'fade'}
              autoplay={{
                delay: 3750,
                disableOnInteraction: true,
              }}
              pagination={{
                clickable: false,
              }}
              modules={[Autoplay, EffectFade]}
            >
              {data.slides?.map((card, idx) => (
                <SwiperSlide key={idx}>
                  <Image
                    className={classes.slideImage}
                    src={card.slideDetails.img.src}
                    alt="ZenAdmin works"
                    height={card.slideDetails.img.height}
                    width={card.slideDetails.img.width}
                  />
                </SwiperSlide>
              ))}
              <HiddenSwiperController activeSlide={activeSlide} />
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  )
}

const HiddenSwiperController = ({activeSlide}: {activeSlide: number}) => {
  const swiper = useSwiper()

  React.useEffect(() => {
    swiper?.slideTo(activeSlide, 500)
    swiper?.autoplay?.start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlide])

  return <></>
}

const LoaderBar = ({handleIndexChange}: {handleIndexChange: () => void}) => {
  React.useEffect(() => {
    const interval = setInterval(() => {
      handleIndexChange()
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className={classes.progressBarContainer}>
      <div className={classes.progressBar} />
      <motion.div
        className={classes.progressBarPseudo}
        animate={{
          width: ['0%', '100%'],
        }}
        transition={{
          duration: 4,
          ease: 'easeIn',
        }}
      ></motion.div>
    </div>
  )
}
